import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const style = document.createElement('style');
style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* Sky-blue palette (Home02) */
    --navy:        #0c2340;
    --navy-2:      #1e3a8a;
    --navy-3:      #3b82f6;
    /* "gold" tokens are repurposed as blue accents so existing pages inherit the new look */
    --gold:        #1e3a8a;
    --gold-light:  #eaf4ff;
    --gold-mid:    #93c5fd;
    --bg:          #dbeeff;
    --card:        #ffffff;
    --text:        #0c2340;
    --text-2:      #3d5166;
    --text-3:      #8a9ab0;
    --border:      #c9dff7;
    --border-2:    #e6f1ff;
    --success:     #1a6640;
    --success-bg:  #edf7f1;
    --danger:      #b91c1c;
    --danger-bg:   #fef2f2;
    --warning:     #92400e;
    --warning-bg:  #fffbeb;
    --nav-h:       60px;
    --radius:      14px;
    --radius-sm:   8px;
    --radius-lg:   20px;
    --shadow-sm:   0 1px 4px rgba(12,35,64,0.06);
    --shadow:      0 2px 14px rgba(12,35,64,0.08);
    --shadow-lg:   0 6px 32px rgba(12,35,64,0.13);
  }

  html {
    scroll-padding-top: var(--nav-h);
  }

  body {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background:
      radial-gradient(ellipse 80% 50% at 20% 0%, rgba(56,132,255,0.14), transparent 60%),
      radial-gradient(ellipse 60% 50% at 80% 100%, rgba(12,35,64,0.06), transparent 60%),
      linear-gradient(180deg, #e6f3ff 0%, #c9e3ff 100%);
    background-attachment: fixed;
    color: var(--text);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    min-height: 100dvh;
  }

  h1, h2, h3 { font-family: 'DM Sans', -apple-system, sans-serif; }

  input, textarea, select, button { font-family: 'DM Sans', sans-serif; }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes bounce  { 0%,80%,100% { transform: scale(0.55); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
  @keyframes pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes float   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }

  .fade-in   { animation: fadeUp 0.28s ease forwards; }
  .msg-group { animation: fadeUp 0.22s ease forwards; }
  .float     { animation: float 3s ease-in-out infinite; }

  .feature-card {
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
    cursor: pointer;
  }
  .feature-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg) !important;
    border-color: var(--gold-mid) !important;
  }

  .nice-input:focus {
    border-color: var(--navy-2) !important;
    box-shadow: 0 0 0 3px rgba(26,58,92,0.1) !important;
    outline: none !important;
  }

  .primary-btn {
    transition: filter 0.15s, transform 0.15s, box-shadow 0.15s;
  }
  .primary-btn:hover:not(:disabled) {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(12,35,64,0.22) !important;
  }
  .primary-btn:active:not(:disabled) { transform: translateY(0); }
  .primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .section-cap {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--text-3);
    font-family: 'DM Sans', sans-serif;
  }

  .dash-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  @media (max-width: 768px) {
    .dash-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
  }
  @media (max-width: 480px) {
    .dash-grid { grid-template-columns: 1fr; gap: 12px; }
  }

  .resume-layout {
    display: grid;
    grid-template-columns: 440px 1fr;
    gap: 0;
  }
  @media (max-width: 900px) {
    .resume-layout { grid-template-columns: 1fr; }
  }

  @media (max-width: 768px) {
    .builder-mobile-notice {
      display: flex !important;
    }
    .builder-wrap {
      flex-direction: column !important;
      height: auto !important;
      overflow: visible !important;
      min-height: calc(100dvh - var(--nav-h));
    }
    .builder-form-panel {
      width: 100% !important;
      height: auto !important;
      overflow-y: visible !important;
      border-right: none !important;
      border-bottom: 1px solid var(--border) !important;
    }
    .builder-form-header {
      position: sticky !important;
      top: var(--nav-h) !important;
      z-index: 10 !important;
    }
    .builder-preview-panel {
      display: none !important;
    }
  }
`;
document.head.appendChild(style);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
